import {
  ENDPOINT_CLUSTERS_BY_ID,
  ENDPOINT_CROP_GENERATIONS,
  ENDPOINT_CROP_GENERATIONS_BY_PLANT_ID,
  ENDPOINT_CROP_TYPES,
  ENDPOINT_CROP_TYPES_BY_PLANT,
  ENDPOINT_GRAIN_PARTIES,
  ENDPOINT_GRAIN_PARTIES_SELECT,
  ENDPOINT_PARTIES,
  ENDPOINT_PLANTS_GRAIN,
  ENDPOINT_SELECT_PARTIES,
  GrainStorageTypeEnum,
} from '@/common/constants';
import { HarvestType } from '@/common/constants/harvest.constants';
import {
  PartiesEnum,
  StorageTypeEnum,
} from '@/common/constants/parties.constants';
import { HttpApi } from '@/common/http';
import i18n from '@/i18n';
import { create } from 'zustand';
import {
  CropGenerationsByPlantId,
  CropTypesByPlant,
  GrainPlant,
  IClusterData,
  ICropData,
  ICropGeneration,
  ICropType,
  IPartiesAction,
  IPartiesState,
  IParty,
  IPartyResponse,
  SelectParties,
} from '../models';

const httpApi = new HttpApi();

export interface IPartiesGrainState extends IPartiesState {
  partiesGrainSelect: any[];
  partiesGrainSelectGoods: any[];
  storageTypesByAnalysis: { value: string; label: string }[];
}

const usePartyGrainStore = create<IPartiesGrainState & IPartiesAction>(
  (set) => ({
    parties: null,
    partiesGrainSelect: [],
    partiesGrainSelectGoods: [],
    plantsGrain: [],
    cropTypesByPlant: [],
    cropGenerationsByPlantId: [],
    partiesSelect: [],
    partiesSelectGeneral: [],
    partiesLoading: true,
    party: [],
    clusters: [],
    partyLoading: true,
    cropTypes: [],
    cropData: {} as ICropData,
    partyDetail: null,
    partiesPagination: {
      totalElements: 0,
      totalPages: 0,
      page: 1,
      size: 10,
    },
    setPartyDetail: (party) =>
      set(() => ({
        partyDetail: party,
      })),
    generationTypes: [
      {
        value: PartiesEnum.SEED,
        label: i18n.t(`parties.generation.${PartiesEnum.SEED}`)!,
      },
      {
        value: PartiesEnum.TECHNICAL,
        label: i18n.t(`parties.generation.${PartiesEnum.TECHNICAL}`)!,
      },
    ],
    storageTypesByAnalysis: [
      {
        label: "Urug'lik",
        value: StorageTypeEnum.SEED,
      },
      {
        label: 'Tovar',
        value: StorageTypeEnum.GOODS,
      },
    ],
    storageTypesByGoods: [
      {
        value: GrainStorageTypeEnum.STORAGE,
        label: 'Ijara',
      },
      {
        value: GrainStorageTypeEnum.STATE_RESERVE,
        label: 'Zaxira',
      },
    ],
    storageTypesBySeed: [
      {
        value: StorageTypeEnum.SEED,
        label: "Urug'lik",
      },
    ],
    storageTypes: [
      {
        value: GrainStorageTypeEnum.STORAGE,
        label: 'Vaqtincha saqlash',
      },
      {
        value: GrainStorageTypeEnum.STATE_RESERVE,
        label: 'Zaxira',
      },
      {
        value: StorageTypeEnum.SEED,
        label: "Urug'lik",
      },
    ],
    cropGenerations: [],
    industrialGrades: Array.from({ length: 5 }, (_, i) => ({
      value: String(i + 1),
      label: String(i + 1),
    })),
    classListByGrain: Array.from({ length: 5 }, (_, i) => ({
      value: String(i + 1),
      label: String(i + 1),
    })),

    fetchCropTypes: async () => {
      try {
        const response = await httpApi.get<ICropType[]>(ENDPOINT_CROP_TYPES);
        set({
          cropTypes: response.map((el) => ({
            value: String(el.id),
            label: el.name,
          })),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
    fetchCropTypesById: async (id: string) => {
      try {
        const response = await httpApi.get<ICropData>(
          ENDPOINT_CROP_TYPES + `/${id}`,
        );
        set({
          cropData: response,
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
    fetchClusters: async () => {
      try {
        const response = await httpApi.get<IClusterData[]>(
          ENDPOINT_CLUSTERS_BY_ID,
        );
        set({
          clusters: response.map(({ id, name, ...rest }) => {
            return {
              value: id,
              label: name,
              ...rest,
            };
          }),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
    fetchPartiesGrain: async (params?: Record<string, string | number>) => {
      set({ partiesLoading: true });
      try {
        const response = await httpApi.get<IPartyResponse>(
          '/api/grain-parties/page',
          params,
        );

        set({
          parties: {
            total: response?.total,
            content: response?.content,
          },
          partiesPagination: {
            totalElements: response?.totalElements,
            totalPages: response?.totalPages,
            page: response.page,
            size: response?.size!,
          },
          partiesGrainSelect: response?.content?.map((el) => {
            return {
              value: el.id,
              label: `${el?.place_number} - ${el?.party_number}`,
            };
          }),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      } finally {
        setTimeout(() => {
          set({ partiesLoading: false });
        }, 300);
      }
    },

    fetchPartiesSelect: async (
      crop_type_id?: string | undefined,
      harvest_type?:
        | HarvestType.COMBINE
        | HarvestType.GROUND_HARVESTED
        | HarvestType.MANUAL,
    ) => {
      try {
        const response = await httpApi.get<SelectParties[]>(
          ENDPOINT_SELECT_PARTIES,
          {
            crop_type_id,
            harvest_type,
          },
        );
        set({
          partiesSelectGeneral: response,
        });
      } catch (e) {
        console.error('Failed to fetch parties:', e);
        throw e;
      }
    },
    fetchPartiesSelectGrain: async (
      params?: Record<string, string | number>,
    ) => {
      try {
        const response = await httpApi.get<any[]>(
          ENDPOINT_GRAIN_PARTIES_SELECT,
          params,
        );
        set({
          partiesGrainSelect: response?.map((el) => {
            return {
              value: el?.id,
              label: `${el?.place_number} - ${el?.party_number}`,
            };
          }),
          partiesGrainSelectGoods: response?.map((el) => {
            return {
              value: el?.id,
              label: `${el?.place_name} - ${el?.place_number}`,
            };
          }),
        });
      } catch (e) {
        console.error('Failed to fetch parties:', e);
        throw e;
      }
    },
    fetchPartyGrain: async (id: string) => {
      return await httpApi.get<IParty>('/api/grain-parties/get-one' + '/' + id);
    },
    fetchPartyByCrop: async (id: string) => {
      set({ partyLoading: true });
      const response = await httpApi.get<IParty[]>(ENDPOINT_PARTIES, {
        crop_type_id: id,
      });
      set({ party: response });
      setTimeout(() => {
        set({ partyLoading: false });
      }, 300);
    },
    fetchCropGenerations: async (type?: string) => {
      const response = await httpApi.get<ICropGeneration[]>(
        ENDPOINT_CROP_GENERATIONS,
        { type },
      );
      set({
        cropGenerations: response.map((el) => ({
          value: String(el.id),
          label: el.name,
        })),
      });
    },
    addPartyGrain: async (party: IParty) => {
      set({
        isCreateLoading: true,
      });
      try {
        await httpApi.post(ENDPOINT_GRAIN_PARTIES, party);
      } catch (error) {
        console.error('Failed to add party:', error);
        throw error;
      } finally {
        set({ isCreateLoading: false });
      }
    },

    updatePartyGrain: async (id: string, data: Partial<IParty>) => {
      set({
        isUpdateLoading: true,
      });
      try {
        await httpApi.put(ENDPOINT_GRAIN_PARTIES + '/' + id, data);
      } catch (error) {
        console.error('Failed to update party:', error);
        throw error;
      } finally {
        set({ isUpdateLoading: false });
      }
    },

    deletePartyGrain: async (id: string) => {
      try {
        await httpApi.delete(ENDPOINT_GRAIN_PARTIES + '/' + id);
      } catch (error) {
        console.error('Failed to delete party:', error);
        throw error;
      }
    },
    fetchPlantsGrain: async () => {
      try {
        const response = await httpApi.get<GrainPlant[]>(ENDPOINT_PLANTS_GRAIN);
        set({
          plantsGrain: response?.map((el) => ({
            value: String(el?.id),
            label: el?.name,
          })),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
    fetchCropTypesByPlant: async (id?: string) => {
      try {
        const response = await httpApi.get<CropTypesByPlant[]>(
          ENDPOINT_CROP_TYPES_BY_PLANT,
          {
            plant_id: id,
          },
        );
        set({
          cropTypesByPlant: response?.map((el) => ({
            value: String(el?.id),
            label: el?.name,
          })),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
    fetchCropGenerationsByPlantId: async (id: string) => {
      try {
        const response = await httpApi.get<CropGenerationsByPlantId[]>(
          ENDPOINT_CROP_GENERATIONS_BY_PLANT_ID.replace('{plant_id}', id),
        );
        set({
          cropGenerationsByPlantId: response?.map(({ id, name, ...rest }) => ({
            value: String(id),
            label: name,
            ...rest,
          })),
        });
      } catch (error) {
        console.error('Failed to fetch parties:', error);
        throw error;
      }
    },
  }),
);

export default usePartyGrainStore;
